// Lightweight client-side fuzzy search over HOW TO topics.
//
// Strategy: tokenize query → score each topic by how many of the tokens
// appear (case-insensitive substring) in title, summary, keywords, or
// the flattened text of every block. Title hits weigh more than summary
// hits which weigh more than block hits. Returns topics that match all
// non-trivial tokens (so "time served letter" filters down to the obvious
// hit instead of returning every topic that contains "letter").
//
// No third-party deps. ~50ms over the full 41-topic index even on a
// budget laptop, which is well below the human-perceptible threshold.

import type { Block, Topic } from "./howToTypes";

interface ScoredTopic {
  topic: Topic;
  score: number;
}

const STOP = new Set([
  "the", "a", "an", "of", "to", "for", "in", "on", "and", "or", "is", "do",
  "i", "how", "my", "me", "with", "from", "by", "at", "as", "be", "are",
  "was", "were", "what", "where", "when", "why", "this", "that",
]);

// Synonyms - when the user types a term, also search for these. Each entry
// is { trigger: [aliases...] }. Tokens are matched after lowercasing +
// stopword removal. Keys are tokens the user might type; values are the
// terms the topic content actually uses.
const SYNONYMS: Record<string, string[]> = {
  // Abbreviations expand to full names
  "bc": ["birth", "certificate"],
  "dl": ["driver", "license"],
  "ss": ["social", "security"],
  "ssa": ["social", "security"],
  "mv": ["mvc", "motor", "vehicle"],
  "mvc": ["motor", "vehicle", "commission"],
  "doc": ["corrections", "department"],
  "joc": ["judgment", "conviction"],
  "lsc": ["legal", "services", "coordinator"],
  "lsnj": ["legal", "services", "new", "jersey"],
  "vlj": ["volunteer", "lawyers", "justice"],
  "pd": ["public", "defender"],
  "sdls": ["senior", "director"],
  "clo": ["chief", "legal", "officer"],
  "muni": ["municipal"],
  "prob": ["probation"],
  "id": ["identification", "state"],
  "pgpa": ["promis", "gavel"],
  "promis": ["promis", "gavel", "criminal"],
  "lrrr": ["license", "restoration", "requirements", "report"],
  "njrc": ["njrc", "reentry"],
  // Concepts → topic-specific terms
  "warrant": ["warrant", "recall"],
  "fines": ["fine", "time", "served", "letter"],
  "ticket": ["ticket", "fine", "mccs"],
  "expungement": ["expungement", "expunge", "record"],
  "expunge": ["expungement"],
  "cleanup": ["clean", "slate"],
  "fingerprint": ["criminal", "record", "njsp"],
  "background": ["criminal", "record"],
  "weed": ["marijuana", "cannabis", "decriminalization"],
  "pot": ["marijuana"],
  "cannabis": ["marijuana", "decriminalization"],
  "homeless": ["fee", "waiver", "indigent"],
  "indigent": ["fee", "waiver", "homeless"],
  "passport": ["passport", "id"],
  "real": ["real", "id"],
  "license": ["license", "driver", "dl"],
  "suspended": ["suspension", "dl", "closed"],
  "surcharge": ["surcharge", "njsvs", "hardship"],
  "puerto": ["puerto", "rico", "pr"],
  "rico": ["puerto", "rico", "pr"],
};

function expandSynonyms(tokens: string[]): string[] {
  const out = new Set(tokens);
  for (const t of tokens) {
    const aliases = SYNONYMS[t];
    if (aliases) for (const a of aliases) out.add(a);
  }
  return Array.from(out);
}

export function searchTopics(topics: Topic[], rawQuery: string): Topic[] {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return topics;
  const rawTokens = q
    .split(/\s+/)
    .map((t) => t.replace(/[^\p{L}\p{N}'-]/gu, ""))
    .filter((t) => t.length > 0 && !STOP.has(t));
  if (rawTokens.length === 0) {
    // The query is all stopwords - fall back to a substring search on the
    // raw query so the user still gets *something*.
    return topics.filter((t) => topicHaystack(t).includes(q));
  }

  // For "matchedAll" we want the original tokens to be matched (each
  // user-typed token must hit something). Synonyms only ADD tokens to
  // boost ranking, not relax the must-match constraint.
  const expandedTokens = expandSynonyms(rawTokens);
  const synonymOnlyTokens = expandedTokens.filter((t) => !rawTokens.includes(t));

  const scored: ScoredTopic[] = [];
  for (const topic of topics) {
    const hay = topicHaystack(topic);
    const titleHay = topic.title.toLowerCase();
    const summaryHay = topic.summary.toLowerCase();
    const kwHay = (topic.keywords || []).join(" ").toLowerCase();

    let score = 0;
    let matchedAll = true;

    // Original tokens - each must hit somewhere (title/summary/keyword/body
    // OR via a synonym that hits anywhere). Without this, a query like
    // "DL" wouldn't return any topic that uses "license" but not "DL".
    for (const tok of rawTokens) {
      let tokScore = 0;
      if (titleHay.includes(tok)) tokScore += 8;
      if (summaryHay.includes(tok)) tokScore += 4;
      if (kwHay.includes(tok)) tokScore += 5;
      if (hay.includes(tok)) tokScore += 1;
      // If the user's token doesn't directly hit, check whether any of
      // its synonyms hit instead. Synonym hit = matchedAll satisfied
      // (with a smaller score contribution).
      if (tokScore === 0) {
        const aliases = SYNONYMS[tok] || [];
        const aliasHit = aliases.some((a) => hay.includes(a));
        if (aliasHit) tokScore = 2;
      }
      if (tokScore === 0) {
        matchedAll = false;
        break;
      }
      score += tokScore;
    }

    if (matchedAll) {
      // Synonym tokens add small bonus weight when they hit, helping
      // disambiguate among multiple equally-matched topics.
      for (const tok of synonymOnlyTokens) {
        if (titleHay.includes(tok)) score += 1.5;
        if (summaryHay.includes(tok)) score += 0.7;
        if (kwHay.includes(tok)) score += 1;
      }
      // Tiny bonus for shorter titles (more specific topics rank higher
      // than broad ones when both match equally).
      score += Math.max(0, 30 - topic.title.length) / 30;
      scored.push({ topic, score });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.map((s) => s.topic);
}

// ─── Haystack: flatten all block text into a single lowercased string ────

const haystackCache = new WeakMap<Topic, string>();

function topicHaystack(topic: Topic): string {
  const cached = haystackCache.get(topic);
  if (cached) return cached;
  const parts: string[] = [topic.title, topic.summary, topic.source];
  if (topic.keywords) parts.push(...topic.keywords);
  for (const b of topic.blocks) parts.push(blockText(b));
  const out = parts.join(" \n ").toLowerCase();
  haystackCache.set(topic, out);
  return out;
}

function blockText(b: Block): string {
  switch (b.kind) {
    case "paragraph":
      return b.text;
    case "steps":
      return [b.heading || "", ...b.items.map((s) => `${s.title} ${s.body || ""}`)].join(" ");
    case "callout":
      return `${b.title || ""} ${b.text}`;
    case "checklist":
      return [b.heading || "", ...b.items].join(" ");
    case "links":
      return [b.heading || "", ...b.items.map((l) => `${l.label} ${l.description || ""}`)].join(" ");
    case "kv":
      return [b.heading || "", ...b.items.map((kv) => `${kv.label} ${kv.value}`)].join(" ");
    case "exhibit":
      return b.caption || "";
    case "divider":
      return b.label || "";
    case "table":
      return [b.heading || "", ...b.columns, ...b.rows.flat()].join(" ");
    case "flowchart":
      return [b.heading || "", ...b.nodes.map((n) => `${n.title} ${n.body || ""}`)].join(" ");
    case "decisionTree":
      return [b.heading || "", flattenDecision(b.root)].join(" ");
    case "timeline":
      return [b.heading || "", ...b.events.map((e) => `${e.when} ${e.title} ${e.body || ""}`)].join(" ");
    case "comparison":
      return [b.heading || "", ...b.options.map((o) => `${o.title} ${o.subtitle || ""} ${o.items.join(" ")}`)].join(" ");
    case "keyPoints":
      return [b.heading || "", ...b.items].join(" ");
  }
}

function flattenDecision(n: import("./howToTypes").DecisionNode): string {
  if (n.kind === "result") return n.label;
  return `${n.question} ${flattenDecision(n.yes)} ${flattenDecision(n.no)}`;
}
